// AdminBots.tsx — /admin/bots. Spec §4: name, publicId (mono), workspace, status chip,
// sources, last crawl, instance id (mono, truncated w/ title). Admin v2 (spec
// docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md §B2) links each
// row's name to the new bot detail page.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { AdminBotsOverviewDto } from '@repo/shared-types';
import { SkeletonText } from '@repo/ui/components/skeleton';
import { StatusChip } from '@repo/ui/components/status-chip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { AdminPagination } from '../components/AdminPagination';
import { apiFetch } from '../lib/api';
import { relativeTime } from '../lib/relative-time';
import { useT } from '../locales/context';

/** Truncate a long id to a readable prefix; the full value stays available via `title`
 * (spec §4: "instance id, mono, truncated w/ title"). */
function truncateId(id: string, head = 12): string {
  return id.length > head ? `${id.slice(0, head)}…` : id;
}

/** Table shape at the real 44px row height (handoff Shell Anatomy §06). */
function TableSkeleton() {
  const widths = ['w-[88%]', 'w-[72%]', 'w-[80%]', 'w-[64%]'];
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="h-[34px] bg-muted" />
      {widths.map((w, i) => (
        <div key={w} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
          <SkeletonText className={w} />
        </div>
      ))}
    </div>
  );
}

export function AdminBots() {
  const t = useT();
  const [page, setPage] = useState(1);

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'bots', page],
    queryFn: () => apiFetch<AdminBotsOverviewDto>(`/api/admin/bots?page=${page}`),
  });

  return (
    <div className="flex flex-col gap-4">
      {isPending && <TableSkeleton />}
      {error && <p className="text-destructive">{error.message}</p>}

      {data && data.items.length === 0 && (
        <p className="text-muted-foreground">{t.admin.bots.noBotsYet}</p>
      )}

      {data && data.items.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.admin.bots.colName}</TableHead>
              <TableHead>{t.admin.bots.colPublicId}</TableHead>
              <TableHead>{t.admin.bots.colWorkspace}</TableHead>
              <TableHead>{t.admin.bots.colStatus}</TableHead>
              <TableHead>{t.admin.bots.colSources}</TableHead>
              <TableHead>{t.admin.bots.colLastCrawl}</TableHead>
              <TableHead>{t.admin.bots.colInstanceId}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((bot) => (
              <TableRow key={bot.id}>
                <TableCell className="font-medium">
                  <Link to={`/admin/bots/${bot.id}`} className="hover:underline">
                    {bot.name}
                  </Link>
                </TableCell>
                <TableCell className="font-mono text-xs">{bot.publicId}</TableCell>
                <TableCell>{bot.workspaceName}</TableCell>
                <TableCell>
                  <StatusChip
                    state={bot.status === 'active' ? 'ready' : 'paused'}
                    label={bot.status}
                  />
                </TableCell>
                <TableCell>{bot.sourcesCount}</TableCell>
                <TableCell>{relativeTime(bot.lastCrawledAt, t.time)}</TableCell>
                <TableCell className="font-mono text-xs" title={bot.aiSearchInstanceId ?? undefined}>
                  {bot.aiSearchInstanceId ? truncateId(bot.aiSearchInstanceId) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {data && <AdminPagination meta={data} onPageChange={setPage} />}
    </div>
  );
}
